import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api, { apiErrorMessage } from '../services/api';
import FloatingInput from '../components/ui/FloatingInput';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import clsx from 'clsx';

function EditProfile() {
  const navigate = useNavigate();
  const { currentUser, updateUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [interests, setInterests] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [initialInterests, setInitialInterests] = useState([]);

  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    bio: '',
    phone: '',
    city: '',
    state: '',
    avatar: '',
  });

  // Track if avatar URL actually loads
  const [avatarValid, setAvatarValid] = useState(false);
  const avatarRef = useRef(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch user profile
        const userResponse = await api.get(
          `/api/users/${currentUser.id || currentUser.ID}`
        );
        // Unwrap the standard { success, message, data } response envelope.
        const userBody = userResponse.data;
        const userData = (userBody && userBody.data) ? userBody.data : userBody;

        // Parse location into city/state if stored as single string
        let city = userData.city || '';
        let state = userData.state || '';
        if (!city && !state && userData.location) {
          const parts = userData.location.split(',').map((s) => s.trim());
          city = parts[0] || '';
          state = parts[1] || '';
        }

        setFormData({
          username: userData.username || '',
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          email: userData.email || '',
          bio: userData.bio || '',
          phone: userData.phone || '',
          city,
          state,
          avatar: userData.avatar || '',
        });

        if (userData.avatar) {
          setAvatarValid(true);
        }

        // Store initial interests so we can diff later
        const userInterests =
          userData.Interests?.map((i) => i.ID || i.id) || [];
        setSelectedInterests(userInterests);
        setInitialInterests(userInterests);

        // Fetch all available interests
        try {
          const interestsResponse = await api.get('/api/interests');
          const ibody = interestsResponse.data;
          const interestsList = Array.isArray(ibody)
            ? ibody
            : (ibody?.data || ibody?.interests || []);
          setInterests(interestsList);
        } catch {
          // Non-critical -- interests can fail silently
        }
      } catch (err) {
        console.error('Failed to load profile data:', err);
        toast.error('Failed to load profile data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Live-validate avatar URL
    if (name === 'avatar') {
      setAvatarValid(false);
    }
  };

  const handleInterestToggle = (interestId) => {
    setSelectedInterests((prev) =>
      prev.includes(interestId)
        ? prev.filter((id) => id !== interestId)
        : [...prev, interestId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      navigate('/login');
      return;
    }

    setSubmitting(true);

    try {
      const userId = currentUser.id || currentUser.ID;

      // Build location string from city + state
      const location = [formData.city, formData.state]
        .filter(Boolean)
        .join(', ');

      // Update user profile
      const profilePayload = {
        username: formData.username,
        first_name: formData.firstName,
        last_name: formData.lastName,
        bio: formData.bio,
        phone: formData.phone,
        location,
        city: formData.city,
        state: formData.state,
        avatar: formData.avatar,
      };

      await api.put(`/api/users/${userId}`, profilePayload);

      // Compute interest additions and removals
      const added = selectedInterests.filter(
        (id) => !initialInterests.includes(id)
      );
      const removed = initialInterests.filter(
        (id) => !selectedInterests.includes(id)
      );

      // Add new interests (the route param is the interest id; the backend
      // attaches it to the authenticated user).
      const addPromises = added.map((interestId) =>
        api.post(`/api/users/${interestId}/interests`)
      );

      // Remove old interests
      const removePromises = removed.map((interestId) =>
        api.delete(`/api/users/${interestId}/interests`)
      );

      await Promise.allSettled([...addPromises, ...removePromises]);

      // Update AuthContext with new user data so the app reflects changes
      updateUser({
        ...currentUser,
        username: formData.username,
        first_name: formData.firstName,
        last_name: formData.lastName,
        bio: formData.bio,
        phone: formData.phone,
        location,
        city: formData.city,
        state: formData.state,
        avatar: formData.avatar,
      });

      toast.success('Profile updated!');
      navigate(`/profile/${userId}`);
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast.error(apiErrorMessage(err, 'Failed to update profile. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="h-8 w-40 skeleton rounded-lg mb-8" />
        <div className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 p-6 space-y-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl skeleton" />
            <div className="flex-1 h-12 skeleton rounded-xl" />
          </div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 skeleton rounded-xl" />
          ))}
          <div className="h-28 skeleton rounded-xl" />
          <div className="flex flex-wrap gap-2">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-8 w-20 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    formData.firstName || formData.username || 'U'
  )}&size=96&background=E6F7FA&color=4CBED9&bold=true`;

  return (
    <motion.div
      className="max-w-2xl mx-auto px-4 sm:px-6 py-8"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Page Header */}
      <h1 className="text-h1 text-surface-900 mb-6">Edit Profile</h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 divide-y divide-surface-100"
      >
        {/* Avatar Section */}
        <div className="p-5 sm:p-6">
          <h2 className="text-h3 text-surface-900 mb-4">Photo</h2>
          <div className="flex items-center gap-5">
            <div className="relative flex-shrink-0">
              <img
                ref={avatarRef}
                src={avatarValid && formData.avatar ? formData.avatar : fallbackAvatar}
                alt="Profile avatar"
                className="w-20 h-20 rounded-2xl object-cover border-2 border-surface-100 bg-surface-50"
                onError={() => {
                  setAvatarValid(false);
                  if (avatarRef.current) {
                    avatarRef.current.src = fallbackAvatar;
                  }
                }}
                onLoad={() => {
                  if (formData.avatar && avatarRef.current?.src === formData.avatar) {
                    setAvatarValid(true);
                  }
                }}
              />
            </div>
            <div className="flex-1">
              <FloatingInput
                label="Avatar URL"
                name="avatar"
                type="url"
                value={formData.avatar}
                onChange={handleChange}
              />
              <p className="text-caption text-surface-400 mt-1.5">
                Paste a link to your profile image
              </p>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="p-5 sm:p-6 space-y-4">
          <h2 className="text-h3 text-surface-900 mb-1">Basic Information</h2>

          <FloatingInput
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FloatingInput
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
            />
            <FloatingInput
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
            />
          </div>

          <FloatingInput
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            disabled
            className="opacity-60"
          />
          <p className="text-caption text-surface-400 -mt-2 pl-1">
            Email cannot be changed
          </p>

          <FloatingInput
            label="Phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
          />

          {/* Bio (textarea -- FloatingInput doesn't support textarea, so manual) */}
          <div className="relative">
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows={4}
              placeholder=" "
              className="peer w-full bg-surface-50 border border-surface-200 rounded-xl text-surface-900 pt-6 pb-3 px-4 focus:ring-2 focus:bg-white focus:outline-none focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-body placeholder-transparent resize-none"
            />
            <label
              htmlFor="bio"
              className={clsx(
                'absolute left-4 transition-all duration-200 pointer-events-none select-none',
                'peer-placeholder-shown:top-4 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400',
                'peer-focus:top-2 peer-focus:text-caption peer-focus:text-primary-600',
                'top-2 text-caption text-surface-500'
              )}
            >
              Bio
            </label>
          </div>
        </div>

        {/* Location */}
        <div className="p-5 sm:p-6 space-y-4">
          <h2 className="text-h3 text-surface-900 mb-1">Location</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FloatingInput
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
            />
            <FloatingInput
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Interests */}
        {interests.length > 0 && (
          <div className="p-5 sm:p-6">
            <h2 className="text-h3 text-surface-900 mb-1">Interests</h2>
            <p className="text-body-sm text-surface-400 mb-4">
              Select topics you are interested in
            </p>
            <div className="flex flex-wrap gap-2">
              {interests.map((interest) => {
                const interestId = interest.ID || interest.id;
                const isActive = selectedInterests.includes(interestId);
                return (
                  <button
                    key={interestId}
                    type="button"
                    onClick={() => handleInterestToggle(interestId)}
                    className={clsx(
                      'px-3.5 py-1.5 rounded-xl text-body-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30',
                      isActive
                        ? 'bg-primary-500 text-white shadow-colored'
                        : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                    )}
                  >
                    {interest.Name || interest.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-5 sm:p-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() =>
              navigate(`/profile/${currentUser.id || currentUser.ID}`)
            }
            className="inline-flex items-center gap-2 px-5 py-2.5 text-body-sm font-semibold text-surface-700 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={clsx(
              'inline-flex items-center gap-2 px-6 py-2.5 text-body-sm font-semibold text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-all duration-200',
              submitting
                ? 'bg-primary-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
            )}
          >
            {submitting && (
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {submitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </motion.div>
  );
}

export default EditProfile;
